import type { PagedRecord } from '@bms2/shared';
import { reportCaughtInBrowser } from '../../shell/systemErrors';
import type { TileAnswer } from './tileItems';

/**
 * 🔒 Release on a Files list (piece 9; T3548, decision 11480): which record
 * kinds offer it, and the companies the one Release window lists for each.
 * The server's release rules name the file's owner and the companies it is
 * released to; this module only says which lists carry the act and what the
 * page already knows of those companies, and the server decides the rest.
 */

/** One company the Release window lists, by id and by the name the page shows it under. */
export interface ReleaseCompany {
  id: string;
  name: string;
}

/** The companies a Files list hands the Release window, and which of them is the customer that needs no NDA. */
export interface ReleaseCompanies {
  companies: ReleaseCompany[];
  customerCompanyId: string | null;
}

/**
 * 🔒 The record kinds whose Files list offers Release (decision 11480): the
 * Deal, the account's Company and a Location on their record pages, and the
 * Bill and the Purchase Order on their document pages. 🔨 A Person's files offer
 * none: the server releases a Person's file to no company, so the act would
 * only ever be refused. Every other kind offers none.
 */
export const RELEASE_FILE_KINDS = ['Deal', 'CompanyRecord', 'LocationRecord', 'Bill', 'PurchaseOrder'] as const;
export type ReleaseFileKind = (typeof RELEASE_FILE_KINDS)[number];

export function offersRelease(recordType: string): recordType is ReleaseFileKind {
  return (RELEASE_FILE_KINDS as readonly string[]).includes(recordType);
}

/** What a record page's answer carries that the targets are read from. */
export interface ReleasePageAnswer {
  recordType: PagedRecord;
  recordId: string;
  title: string;
  tiles: readonly TileAnswer[];
  facts: Readonly<Record<string, string | boolean | null>>;
}

/**
 * 🔒 The Release window's companies on a record page (decision 11480). The
 * Deal lists its account and every contact's company, which its answer
 * carries; the Company lists itself, the account its files sit on; a Location
 * lists the company of each open occupancy, the first as the account.
 */
export function releaseCompanies(answer: ReleasePageAnswer): ReleaseCompanies {
  if (answer.recordType === 'CompanyRecord') return { companies: [{ id: answer.recordId, name: answer.title }], customerCompanyId: answer.recordId };
  if (answer.recordType === 'LocationRecord') {
    const seen = new Map<string, string>();
    for (const row of answer.tiles.find((tile) => tile.key === 'occupants')?.rows ?? []) {
      const companyId = row.raw?.companyId;
      if (row.raw?.ended === true || typeof companyId !== 'string' || seen.has(companyId)) continue;
      seen.set(companyId, row.label);
    }
    const companies = [...seen].map(([id, name]) => ({ id, name }));
    return { companies, customerCompanyId: companies[0]?.id ?? null };
  }
  const companyId = typeof answer.facts.companyId === 'string' ? answer.facts.companyId : null;
  return { companies: dealCompanies(answer, companyId), customerCompanyId: companyId };
}

/** The Deal's account and contacts' companies, a JSON string on its answer (decision 0593); a malformed list falls back to the account alone. */
function dealCompanies(answer: ReleasePageAnswer, companyId: string | null): ReleaseCompany[] {
  const raw = answer.facts.releaseTargets;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as ReleaseCompany[];
      if (Array.isArray(parsed)) return parsed;
    } catch (error) {
      reportCaughtInBrowser('records.accountList.parse', error);
      // A malformed list falls back to the Account alone.
    }
  }
  return companyId ? [{ id: companyId, name: String(answer.facts.companyName ?? '') }] : [];
}

/** 🔒 A vendor document's one target, its vendor, the account its files sit on (decision 11480). */
export function vendorReleaseCompanies(vendorCompanyId: string | null | undefined, vendorName: string | null | undefined): ReleaseCompanies {
  if (!vendorCompanyId) return { companies: [], customerCompanyId: null };
  return { companies: [{ id: vendorCompanyId, name: vendorName ?? '' }], customerCompanyId: vendorCompanyId };
}
